/**
 * Members Table Column Definitions Hook
 *
 * Provides column definitions for the unified members table
 * that handles both members and pending invitations.
 */

import { useMemo } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { Mail, Clock, RefreshCw, Trash2, X } from 'lucide-react';
import { Button } from '@loader/ui';
import { Badge } from '@loader/ui';
import { Avatar, AvatarFallback, AvatarImage } from '@loader/ui';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@loader/ui';
import type { WorkspaceRole } from '@loader/shared-types';

import { RoleBadge } from '@/components/common';
import type { WorkspaceMember, PendingInvitation } from '@/lib/types';

// ============================================================================
// Types
// ============================================================================

/**
 * Unified row type for displaying both members and pending invitations
 * Uses discriminated union for type-safe handling
 */
export type UnifiedMemberRow =
  | {
      type: 'member';
      id: string;
      email: string;
      displayName: string;
      initials: string;
      avatarUrl: string | null;
      role: WorkspaceRole;
      joinedAt: Date | null;
      userId: string;
      isCurrentUser: boolean;
      original: WorkspaceMember;
    }
  | {
      type: 'invitation';
      id: string;
      email: string;
      displayName: string;
      initials: string;
      avatarUrl: null;
      role: WorkspaceRole;
      invitedAt: Date;
      original: PendingInvitation;
    };

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Get user display name from member data
 */
export function getUserDisplayName(member: WorkspaceMember): string {
  if (!member.user) return 'Unknown User';
  const { first_name, last_name, email } = member.user;
  if (first_name && last_name) return `${first_name} ${last_name}`;
  if (first_name) return first_name;
  if (last_name) return last_name;
  return email || 'Unknown User';
}

/**
 * Get user initials for avatar
 */
export function getUserInitials(member: WorkspaceMember): string {
  if (!member.user) return 'U';
  const { first_name, last_name, email } = member.user;
  if (first_name && last_name) {
    return `${first_name[0]}${last_name[0]}`.toUpperCase();
  }
  if (first_name) return first_name[0].toUpperCase();
  if (last_name) return last_name[0].toUpperCase();
  if (email) return email[0].toUpperCase();
  return 'U';
}

/**
 * Get initials from email
 */
export function getEmailInitials(email: string): string {
  return email[0].toUpperCase();
}

/**
 * Get available roles based on current user's role
 */
export function getAvailableRoles(currentRole: WorkspaceRole | null): WorkspaceRole[] {
  if (currentRole === 'owner') {
    return ['viewer', 'editor', 'owner'];
  }
  return [];
}

/**
 * Transform a member into a unified row
 * Handles both active members (clerk_user_id set) and pending invitations (clerk_user_id null)
 */
export function memberToUnifiedRow(
  member: WorkspaceMember,
  currentUserId: string | null
): UnifiedMemberRow {
  // Check if this is a pending invitation (partial user without clerk_user_id)
  const isPending = !member.user?.clerk_user_id;

  if (isPending) {
    return {
      type: 'invitation',
      id: member.id,
      email: member.user?.email || 'Unknown',
      displayName: member.user?.email || 'Unknown',
      initials: getEmailInitials(member.user?.email || 'U'),
      avatarUrl: null,
      role: member.role,
      invitedAt: member.created_at ? new Date(member.created_at) : new Date(),
      original: {
        id: member.user_id, // Use user_id for invitation operations
        email: member.user?.email || '',
        role: member.role,
        created_at: member.created_at,
      },
    };
  }

  return {
    type: 'member',
    id: member.id,
    email: member.user?.email || 'Unknown',
    displayName: getUserDisplayName(member),
    initials: getUserInitials(member),
    avatarUrl: member.user?.profile_image_url ?? null,
    role: member.role,
    joinedAt: member.created_at ? new Date(member.created_at) : null,
    userId: member.user_id,
    isCurrentUser: currentUserId === member.user_id,
    original: member,
  };
}

// ============================================================================
// Hook
// ============================================================================

interface UseMembersColumnsOptions {
  canChangeRoles: boolean;
  currentUserRole: WorkspaceRole | null;
  updatingRoles: Set<string>;
  resendingInvitations: Set<string>;
  onRoleChange: (memberId: string, userId: string, newRole: WorkspaceRole) => void;
  onRemoveMember: (member: WorkspaceMember) => void;
  onResendInvitation: (invitation: PendingInvitation) => void;
  onRevokeInvitation: (invitation: PendingInvitation) => void;
}

/**
 * Hook that provides memoized column definitions for the unified members table
 */
export function useMembersColumns({
  canChangeRoles,
  currentUserRole,
  updatingRoles,
  resendingInvitations,
  onRoleChange,
  onRemoveMember,
  onResendInvitation,
  onRevokeInvitation,
}: UseMembersColumnsOptions): ColumnDef<UnifiedMemberRow>[] {
  return useMemo<ColumnDef<UnifiedMemberRow>[]>(
    () => [
      {
        accessorKey: 'displayName',
        header: 'Member',
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }) => {
          const item = row.original;
          const isPending = item.type === 'invitation';

          return (
            <div className="flex items-center gap-2">
              <Avatar className="h-9 w-9">
                {item.avatarUrl && (
                  <AvatarImage src={item.avatarUrl} alt={item.displayName} />
                )}
                <AvatarFallback className="text-sm">
                  {item.initials}
                </AvatarFallback>
              </Avatar>
              <div className="flex flex-col">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{item.displayName}</span>
                  {isPending && (
                    <Badge variant="outline" className="text-xs">
                      Pending
                    </Badge>
                  )}
                </div>
                <div className="text-muted-foreground flex items-center gap-1 text-sm">
                  <Mail className="h-3 w-3" />
                  <span>{item.email}</span>
                </div>
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: 'role',
        header: 'Role',
        enableSorting: false,
        enableColumnFilter: true,
        filterFn: 'equalsString',
        cell: ({ row }) => {
          const item = row.original;

          // For invitations, show read-only badge
          if (item.type === 'invitation') {
            return <RoleBadge role={item.role} />;
          }

          // For members
          const isUpdating = updatingRoles.has(item.id);
          const isCurrentUser = item.isCurrentUser;

          // Show Select dropdown for admins/owners (unless it's their own role)
          if (canChangeRoles && !isCurrentUser) {
            const availableRoles = getAvailableRoles(currentUserRole);

            return (
              <Select
                value={item.role}
                onValueChange={(value) =>
                  onRoleChange(item.id, item.userId, value as WorkspaceRole)
                }
                disabled={isUpdating}
              >
                <SelectTrigger
                  className="h-8 w-[140px]"
                  disabled={isUpdating}
                >
                  <SelectValue>
                    <RoleBadge role={item.role} />
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {availableRoles.map((availableRole) => (
                    <SelectItem key={availableRole} value={availableRole}>
                      <RoleBadge role={availableRole} />
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            );
          }

          return <RoleBadge role={item.role} />;
        },
      },
      {
        accessorKey: 'status',
        header: 'Status',
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }) => {
          const item = row.original;

          if (item.type === 'invitation') {
            return (
              <div className="text-muted-foreground flex items-center gap-1 text-sm">
                <Clock className="h-3 w-3" />
                <span>Invited {item.invitedAt.toLocaleDateString()}</span>
              </div>
            );
          }

          return (
            <div className="text-muted-foreground text-sm">
              {item.joinedAt ? `Joined ${item.joinedAt.toLocaleDateString()}` : '—'}
            </div>
          );
        },
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }) => {
          const item = row.original;

          // For invitations, show resend and revoke buttons
          if (item.type === 'invitation') {
            const invitation = item.original;
            return (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    onResendInvitation(invitation);
                  }}
                  disabled={resendingInvitations.has(invitation.id)}
                >
                  {resendingInvitations.has(invitation.id) ? (
                    <RefreshCw className="mr-1 h-3 w-3 animate-spin" />
                  ) : (
                    <RefreshCw className="mr-1 h-3 w-3" />
                  )}
                  Resend
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRevokeInvitation(invitation);
                  }}
                  title="Revoke invitation"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            );
          }

          // For members
          const member = item.original;
          const canRemove =
            canChangeRoles && !item.isCurrentUser && item.role !== 'owner';

          if (!canRemove) {
            return null;
          }

          return (
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={(e) => {
                e.stopPropagation();
                onRemoveMember(member);
              }}
              title="Remove member"
              aria-label="Remove member"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          );
        },
      },
    ],
    [
      canChangeRoles,
      currentUserRole,
      updatingRoles,
      resendingInvitations,
      onRoleChange,
      onRemoveMember,
      onResendInvitation,
      onRevokeInvitation,
    ]
  );
}
